import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import Pagination from "@/components/Pagination";

const PAGE_SIZE = 50;
const ACTIONS = ["login", "import", "create", "update", "approve", "reorder", "delete", "password", "permissions"];
const COLORS = {
  login: "bg-blue-50 text-blue-700 border-blue-300",
  import: "bg-purple-50 text-purple-700 border-purple-300",
  delete: "bg-red-50 text-red-700 border-red-300",
  approve: "bg-green-50 text-green-700 border-green-300",
  password: "bg-yellow-50 text-yellow-800 border-yellow-300",
};
const colorFor = a => COLORS[Object.keys(COLORS).find(k => a.startsWith(k))] || "bg-slate-50 text-slate-700 border-slate-300";

function Details({ snapshot }) {
  const entries = Object.entries(snapshot || {});
  if (!entries.length) return <span className="text-slate-300">—</span>;
  const text = entries.map(([k, v]) => `${k}: ${typeof v === "object" ? JSON.stringify(v) : v}`).join(" · ");
  return <span className="text-xs text-slate-600 line-clamp-2 break-all" title={text}>{text}</span>;
}

export default function AuditLogPanel() {
  const [action, setAction] = useState("all");
  const [q, setQ] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [data, setData] = useState({ items: [], total: 0 });

  useEffect(() => { const t = setTimeout(() => setSearch(q.trim()), 300); return () => clearTimeout(t); }, [q]);
  useEffect(() => { setPage(0); }, [action, search]);
  useEffect(() => {
    const params = { page, page_size: PAGE_SIZE };
    if (action !== "all") params.action = action;
    if (search) params.search = search;
    api.get("/audit-logs", { params }).then(r => setData(r.data)).catch(() => setData({ items: [], total: 0 }));
  }, [page, action, search]);

  return (
    <div className="border border-slate-300 bg-white" data-testid="audit-log-panel">
      <div className="p-4 border-b flex flex-col md:flex-row gap-3 md:items-center">
        <h3 className="font-heading text-2xl mr-auto">AUDIT LOG</h3>
        <Input placeholder="Search user or entity…" value={q} onChange={e => setQ(e.target.value)} className="rounded-sm md:w-64" data-testid="audit-search"/>
        <Select value={action} onValueChange={setAction}>
          <SelectTrigger className="rounded-sm md:w-44" data-testid="audit-action-filter"><SelectValue/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All actions</SelectItem>
            {ACTIONS.map(a => <SelectItem key={a} value={a} className="capitalize">{a}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="overflow-x-auto max-h-[640px]">
        <Table>
          <TableHeader className="bg-slate-100 sticky top-0">
            <TableRow>
              <TableHead>Timestamp</TableHead><TableHead>User</TableHead><TableHead>Action</TableHead>
              <TableHead>Entity</TableHead><TableHead>Details</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.items.length === 0 && (
              <TableRow><TableCell colSpan={5} className="text-center text-slate-500 py-10" data-testid="audit-empty">No audit entries.</TableCell></TableRow>
            )}
            {data.items.map(e => (
              <TableRow key={e.id} data-testid={`audit-row-${e.id}`}>
                <TableCell className="text-xs whitespace-nowrap font-mono">{new Date(e.at).toLocaleString()}</TableCell>
                <TableCell className="text-xs"><div className="font-medium">{e.user_name}</div><div className="text-slate-500">{e.user_email}</div></TableCell>
                <TableCell><Badge className={`rounded-sm border whitespace-nowrap ${colorFor(e.action)}`}>{e.action.replace(/_/g, " ")}</Badge></TableCell>
                <TableCell className="text-xs whitespace-nowrap">{e.entity_type}</TableCell>
                <TableCell className="max-w-md"><Details snapshot={e.snapshot}/></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <Pagination page={page} pageSize={PAGE_SIZE} total={data.total} onPageChange={setPage} testidPrefix="audit-pagination"/>
    </div>
  );
}
